import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { formatCents, formatDollars, toCents } from '../utils/money';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Radio,
  RadioGroup,
  Chip,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  CreditScore as OnFileIcon,
  AccountBalance as BankIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useQuery, useQueryClient } from 'react-query';
import api, { creditsApi } from '../utils/api';
import {
  recordablePaymentTypeOptions,
  paymentTypesLoadState,
  PaymentTypeOption,
} from '../utils/paymentTypes';
import SavePaymentMethodDialog from './SavePaymentMethodDialog';
import SendPaymentLinkDialog from './SendPaymentLinkDialog';
import {
  executeWrite,
  withOfflineFallback,
  readInvoiceBalance,
  skipWhenOffline,
  OfflineWriteBlockedError,
} from '../offline';
import { todayLocalISO, ymdToNoonUtcIso } from '../utils/dateUtils';

const PAYMENT_TYPES = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'payment_link', label: 'Send Payment Link', icon: LinkIcon },
  { value: 'write_off', label: 'Write-Off' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * What the method select offers while GET /payment-types is in flight or has
 * FAILED (offline is the common case — this dialog records cash in the field).
 * Deliberately wider than utils/paymentTypes' STATIC_PAYMENT_TYPES: Write-Off
 * and Other are backend seeds too, and this dialog has always offered them, so
 * an offline vet keeps both. Every entry must stay a value the server seeds,
 * or the fallback offers something assertPaymentTypeAllowed rejects.
 * `payment_link` is excluded because it is a Stripe charge flow, appended
 * separately below and only when Connect is enabled.
 */
const FALLBACK_PAYMENT_TYPES: PaymentTypeOption[] = PAYMENT_TYPES.filter(
  (t) => t.value !== 'payment_link',
).map((t) => ({ value: t.value, label: t.label }));

// BILL-CRED: menu value for "Apply Available Credit". Deliberately NOT a
// slug the payment-types service could ever mint (slugify strips leading and
// trailing underscores) — a clinic-defined type named "Credit"/"Apply Credit"
// must never collide into the apply-credit flow.
export const APPLY_CREDIT_SENTINEL = '__apply_credit__';

const REFERENCE_PLACEHOLDERS: Record<string, string> = {
  check: 'Check #1234',
  credit_card: 'Authorization # (if charged externally)',
  debit_card: 'Authorization # (if charged externally)',
  write_off: 'Reason for write-off',
  other: 'Description or reference',
};

interface SavedPaymentMethod {
  id: string;
  stripePaymentMethodId: string;
  type: string;
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
  isDefault: boolean;
}

interface EnhancedPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  invoice: {
    id: string;
    invoiceNumber: string;
    amount: number;
    horseName?: string;
    clientId?: string;
    clientName?: string;
    dueDate?: string;
    taxAmount?: number;
    horse?: {
      name: string;
      // Both are the SERVER-resolved BILL-TO (billing/bill-to.ts) — the payer's own
      // name/email on a third-party invoice, not the patient's owner. This object is
      // forwarded verbatim to SendPaymentLinkDialog, which reads both legs; `ownerName`
      // was undeclared here, so on this path the compiler could not see it at all (M-2).
      ownerName?: string;
      ownerEmail?: string;
      /** The patient's ownership list. NOT the bill-to — last resort only. */
      owners?: Array<{ name?: string; email?: string }>;
    };
    client?: {
      id?: string;
      name?: string;
      email?: string;
    };
    /** One-time payer snapshot (INV-13) — forwarded to SendPaymentLinkDialog (B1). */
    thirdPartyPayer?: {
      name?: string;
      email?: string;
      phone?: string;
      address?: string;
    } | null;
    paymentLinkUrl?: string;
  };
  stripeConnectEnabled?: boolean;
  /** When true, skip payment type selection and go straight to card entry */
  cardOnly?: boolean;
  /** Specific amount (in dollars) to charge, e.g. from a partial inline payment */
  requestedAmount?: number;
  /** Called when a payment link is sent successfully */
  onPaymentLinkSent?: () => void;
}

interface PaymentIntentResponse {
  clientSecret: string;
  // NULL when the server could not tie a local Payment row to this intent (it retires the orphan
  // claim and lets the webhook book the capture). The intent below is what is always meaningful.
  paymentId: string | null;
  paymentIntentId?: string;
  amount: number;
  currency: string;
  stripeAccountId: string;
}

interface BalanceResponse {
  total: number;
  taxAmount: number;
  totalWithTax: number;
  totalPaid: number;
  totalPaidCents: number;
  processingAmount?: number;
  balanceDue: number;
  balanceDueCents: number;
  payments: Array<{
    id: string;
    amount: number;
    paymentType: string;
    paymentDate: string;
    referenceNote: string | null;
    cardBrand: string | null;
    cardLast4: string | null;
  }>;
}

// --------------- Card Payment Form (Stripe Elements) ---------------
const CardPaymentForm: React.FC<{
  amount: number;
  paymentId: string | null;
  onSuccess: (isProcessing?: boolean) => void;
  onCancel: () => void;
}> = ({ amount, paymentId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'An error occurred');
        setProcessing(false);
        return;
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        setProcessing(false);
        return;
      }

      if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
        try {
          // No local row to confirm: the server told us it could not tie one to this intent, so
          // the webhook books the capture. Posting a null id would just be a refusal we swallow.
          if (paymentId) await api.post('/payments/confirm', { paymentId });
        } catch (confirmErr) {
          // Confirm endpoint may return error if already processed via webhook - safe to ignore
        }
        onSuccess(paymentIntent.status === 'processing');
      } else {
        setError('Payment was not completed. Please try again.');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('An unexpected error occurred');
      setProcessing(false);
    }
  };

  const formatCurrency = formatCents;

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: { applePay: 'never', googlePay: 'never' },
        }}
      />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <DialogActions sx={{ mt: 3, px: 0 }}>
        <Button onClick={onCancel} disabled={processing}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={!stripe || processing}>
          {processing ? (
            <><CircularProgress size={20} sx={{ mr: 1 }} />Processing...</>
          ) : (
            `Charge ${formatCurrency(amount)}`
          )}
        </Button>
      </DialogActions>
    </form>
  );
};

// --------------- On File Payment (Saved Cards / Bank Accounts) ---------------
const OnFilePayment: React.FC<{
  amount: number;
  clientId: string;
  invoiceId: string;
  onSuccess: (isProcessing?: boolean) => void;
  onCancel: () => void;
  onAddNew: () => void;
  refreshKey?: number;
}> = ({ amount, clientId, invoiceId, onSuccess, onCancel, onAddNew, refreshKey }) => {
  const [paymentMethods, setPaymentMethods] = useState<SavedPaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/clients/${clientId}/payment-methods`);
        setPaymentMethods(response.data);
        const defaultMethod = response.data.find((m: SavedPaymentMethod) => m.isDefault);
        if (defaultMethod) {
          setSelectedMethod(defaultMethod.id);
        } else if (response.data.length > 0) {
          setSelectedMethod(response.data[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
        setError('Failed to load saved cards');
      } finally {
        setLoading(false);
      }
    };
    if (clientId) fetchPaymentMethods();
    else setLoading(false);
  }, [clientId, refreshKey]);

  const chargeRequestRef = useRef<{ storageKey: string; requestId: string } | null>(null);

  const handleCharge = async () => {
    if (!selectedMethod) return;
    const storageKey = `st.savedCharge.${invoiceId}.${clientId}.${selectedMethod}.${amount}`;
    let requestId = chargeRequestRef.current?.storageKey === storageKey
      ? chargeRequestRef.current.requestId : null;
    if (!requestId) {
      try { requestId = sessionStorage.getItem(storageKey); } catch { /* use the mounted attempt */ }
      requestId = requestId || crypto.randomUUID();
      chargeRequestRef.current = { storageKey, requestId };
      try { sessionStorage.setItem(storageKey, requestId); } catch { /* keep the in-memory retry key */ }
    }
    const finishAttempt = () => {
      chargeRequestRef.current = null;
      try { sessionStorage.removeItem(storageKey); } catch { /* no persisted key */ }
    };
    setProcessing(true);
    setError(null);
    try {
      const payload: { invoiceId: string; amount?: number; requestId: string } = { invoiceId, requestId };
      if (amount > 0) payload.amount = amount;
      const response = await api.post(`/clients/${clientId}/payment-methods/${selectedMethod}/charge`, payload);
      if (response.data.status === 'succeeded') {
        finishAttempt();
        onSuccess();
      } else if (response.data.status === 'processing') {
        finishAttempt();
        onSuccess(true);
      } else if (response.data.status === 'requires_action') {
        setError('This card requires additional authentication. Please use a new card instead.');
        setProcessing(false);
      } else {
        setError('Payment failed. Please try again.');
        setProcessing(false);
      }
    } catch (err: any) {
      console.error('Charge error:', err);
      if (err.response?.data?.code === 'PAYMENT_DECLINED') finishAttempt();
      setError(err.response?.data?.message || 'Payment failed');
      setProcessing(false);
    }
  };

  const formatCurrency = formatCents;

  const getPaymentMethodLabel = (method: SavedPaymentMethod): string => {
    if (method.type === 'us_bank_account') {
      return method.cardBrand || 'Bank Account';
    }
    switch (method.cardBrand?.toLowerCase()) {
      case 'visa': return 'Visa';
      case 'mastercard': return 'Mastercard';
      case 'amex': return 'Amex';
      case 'discover': return 'Discover';
      default: return 'Card';
    }
  };

  const isBank = (method: SavedPaymentMethod) => method.type === 'us_bank_account';

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  if (paymentMethods.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          <CreditCardIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
          <Typography variant="body1" gutterBottom>No saved payment methods on file</Typography>
          <Typography variant="body2" color="text.secondary">
            Add a card or bank account to this client's profile for faster payments
          </Typography>
        </Box>
        <DialogActions sx={{ mt: 2, px: 0 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="outlined" onClick={onAddNew}>Add New Payment Method</Button>
        </DialogActions>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a saved payment method to charge
      </Typography>
      <RadioGroup value={selectedMethod} onChange={(e) => setSelectedMethod(e.target.value)}>
        <List disablePadding>
          {paymentMethods.map((method) => (
            <ListItem
              key={method.id}
              sx={{
                border: '1px solid',
                borderColor: selectedMethod === method.id ? 'primary.main' : 'divider',
                borderRadius: 1,
                mb: 1,
                cursor: 'pointer',
                bgcolor: selectedMethod === method.id ? 'action.selected' : 'transparent',
              }}
              onClick={() => setSelectedMethod(method.id)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Radio checked={selectedMethod === method.id} value={method.id} size="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isBank(method)
                      ? <BankIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      : <CreditCardIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    }
                    <Typography variant="body1">
                      {getPaymentMethodLabel(method)} •••• {method.cardLast4}
                    </Typography>
                    {method.isDefault && (
                      <Chip label="Default" size="small" color="primary" variant="outlined" />
                    )}
                  </Box>
                }
                secondary={
                  !isBank(method) && method.cardExpMonth && method.cardExpYear
                    ? `Expires ${method.cardExpMonth.toString().padStart(2, '0')}/${method.cardExpYear}`
                    : isBank(method) ? 'Bank Account (ACH)' : undefined
                }
              />
            </ListItem>
          ))}
        </List>
      </RadioGroup>
      <Button size="small" onClick={onAddNew} sx={{ mt: 1 }}>+ Add New Payment Method</Button>
      <DialogActions sx={{ mt: 3, px: 0 }}>
        <Button onClick={onCancel} disabled={processing}>Cancel</Button>
        <Button variant="contained" onClick={handleCharge} disabled={!selectedMethod || processing}>
          {processing ? (
            <><CircularProgress size={20} sx={{ mr: 1 }} />Processing...</>
          ) : (
            `Charge ${formatCurrency(amount)}`
          )}
        </Button>
      </DialogActions>
    </Box>
  );
};

// --------------- Main Dialog ---------------
const EnhancedPaymentDialog: React.FC<EnhancedPaymentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  invoice,
  stripeConnectEnabled = false,
  cardOnly = false,
  requestedAmount,
  onPaymentLinkSent,
}) => {
  const queryClient = useQueryClient();
  const [paymentType, setPaymentType] = useState(cardOnly ? 'credit_card' : 'cash');
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [referenceNote, setReferenceNote] = useState('');
  const [payerName, setPayerName] = useState('');
  // PAY-8: the clinic's configurable payment types for the method dropdown.
  // This is a React Query on the SHARED ['payment-types'] key (the Invoicing
  // screen and the client invoice wizard read the same one, so the three
  // dedupe into one request) rather than the useState + useEffect fetch it
  // replaces. The fetch had no load state, so an empty `clinicTypes` meant
  // BOTH "still in flight / offline" and "this clinic deactivated every type",
  // and the fallback below could not tell them apart.
  const clinicPaymentTypesQuery = useQuery<PaymentTypeOption[]>(
    ['payment-types'],
    () => api.get('/payment-types').then((r) => r.data),
    { enabled: open },
  );
  const clinicTypes = clinicPaymentTypesQuery.data;
  const paymentTypesState = paymentTypesLoadState(clinicPaymentTypesQuery);
  const [processing, setProcessing] = useState(false);
  // credit-integrity F3: apply-credit idempotency key (see the mint site in the submit path)
  const applyKeyRef = useRef<{ invoiceId: string; amountCents: number; key: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Stripe card payment state
  const [stripeMode, setStripeMode] = useState<'new_card' | 'on_file' | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentIntentResponse | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [saveCardDialogOpen, setSaveCardDialogOpen] = useState(false);
  const [refreshCardsKey, setRefreshCardsKey] = useState(0);
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkAmount, setPaymentLinkAmount] = useState('');
  // Set once the link window has sent: its Done then closes this dialog as well.
  const [paymentLinkSent, setPaymentLinkSent] = useState(false);

  // BILL-CRED: the client's available account credit (ledger-computed, dollars)
  const [creditBalance, setCreditBalance] = useState(0);
  // Distinguish "no credit" from "couldn't load credit" — a swallowed fetch
  // failure would silently hide the Apply-Credit option for a client who has
  // a real balance (fail-loud-when-online rule for money data).
  const [creditFetchFailed, setCreditFetchFailed] = useState(false);

  const initializedForInvoiceRef = React.useRef<string | null>(null);
  const isInitializingRef = React.useRef(false);

  const isCardType = paymentType === 'credit_card' || paymentType === 'debit_card';
  const isPaymentLink = paymentType === 'payment_link';
  // BILL-CRED: sentinel that payment-type slugify (strips leading/trailing
  // underscores) can NEVER produce — a clinic-defined type must not be able to
  // collide into the apply-credit flow.
  const isCreditApply = paymentType === APPLY_CREDIT_SENTINEL;
  const useStripeFlow = isCardType && stripeConnectEnabled;

  // The clinic-configured methods this dialog may RECORD against. The built-in
  // fallback applies only while GET /payment-types is in flight or has failed;
  // a LOADED-empty list means the clinic deactivated every type, and each
  // built-in would then 400 in assertPaymentTypeAllowed, so we offer none.
  const recordableClinicTypes = useMemo(
    () =>
      recordablePaymentTypeOptions(clinicTypes, paymentTypesState, FALLBACK_PAYMENT_TYPES).filter(
        // Defensive: slugify can never mint the sentinel, and
        // recordablePaymentTypeOptions already drops the reserved 'credit'.
        (t) => t.value !== APPLY_CREDIT_SENTINEL,
      ),
    [clinicTypes, paymentTypesState],
  );
  const noRecordableClinicTypes = recordableClinicTypes.length === 0;

  // Everything the method select offers, in render order: the clinic's
  // recordable types, then the two synthetic FLOWS — a Stripe payment link and
  // an account-credit application. Neither is a ClinicPaymentType, so both
  // survive a clinic that has no active payment type at all.
  //
  // Deliberately NOT memoised: `skipWhenOffline()` is read, not subscribed to,
  // so this has to be re-evaluated on every render exactly as the inline list
  // it replaces was.
  const methodOptions: PaymentTypeOption[] = [
    ...recordableClinicTypes,
    ...(stripeConnectEnabled ? [{ value: 'payment_link', label: 'Send Payment Link' }] : []),
    // BILL-CRED: settle from the client's account-credit balance. Only offered
    // when the client actually has credit (online-only).
    ...(invoice.clientId && creditBalance > 0 && skipWhenOffline()
      ? [
          {
            value: APPLY_CREDIT_SENTINEL,
            label: `Apply Available Credit (${formatDollars(creditBalance)})`,
          },
        ]
      : []),
  ].filter(
    // Offline: card + payment-link need Stripe (a live connection). Cash/check/
    // custom manual types record locally and sync.
    (type) =>
      skipWhenOffline() || !['credit_card', 'debit_card', 'payment_link'].includes(type.value),
  );
  // Stable deps for the snap-to-a-valid-selection effect below (both arrays are
  // a fresh identity every render). The two keys are NOT interchangeable there:
  // everything on offer may KEEP a selection, but only a recordable clinic type
  // may become one automatically — see the effect.
  const methodOptionsKey = methodOptions.map((o) => o.value).join('|');
  const recordableTypesKey = recordableClinicTypes.map((o) => o.value).join('|');

  // Fetch balance when dialog opens
  useEffect(() => {
    if (!open || !invoice.id) return;
    const fetchBalance = async () => {
      setBalanceLoading(true);
      try {
        // Offline: compute the balance from the replica (synced payments are
        // stored in cents, mirroring the server math) so partial payments are
        // respected in the field, not just invoice.amount.
        const data = await withOfflineFallback(
          () => api.get(`/payments/invoice/${invoice.id}/balance`).then((r) => r.data),
          () => readInvoiceBalance(invoice.id),
        )();
        const response = { data };
        setBalance(response.data);
        const defaultAmount = requestedAmount ?? response.data.balanceDue ?? invoice.amount;
        setAmount(defaultAmount.toFixed(2));
        setPaymentLinkAmount(defaultAmount.toFixed(2));
      } catch (err) {
        console.error('Failed to fetch balance:', err);
        const defaultAmount = requestedAmount ?? invoice.amount;
        setAmount(defaultAmount.toFixed(2));
        setPaymentLinkAmount(defaultAmount.toFixed(2));
      } finally {
        setBalanceLoading(false);
      }
    };
    fetchBalance();
    setPaymentDate(todayLocalISO());
  }, [open, invoice.id, invoice.amount, requestedAmount]);

  const initializeCardPayment = useCallback(async () => {
    if (!open || !invoice.id || !useStripeFlow || stripeMode !== 'new_card') return;
    if (initializedForInvoiceRef.current === invoice.id && paymentData) return;
    if (isInitializingRef.current) return;

    isInitializingRef.current = true;
    setCardLoading(true);
    setError(null);

    try {
      const chargeAmountCents = requestedAmount ? toCents(requestedAmount) : undefined;
      const response = await api.post('/payments/create-intent', { invoiceId: invoice.id, amount: chargeAmountCents });
      const data: PaymentIntentResponse = response.data;
      setPaymentData(data);
      initializedForInvoiceRef.current = invoice.id;

      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!publishableKey) throw new Error('Stripe publishable key not configured');

      setStripePromise(loadStripe(publishableKey, { stripeAccount: data.stripeAccountId }));
    } catch (err: any) {
      console.error('Failed to initialize payment:', err);
      setError(err.response?.data?.message || 'Failed to initialize card payment.');
    } finally {
      setCardLoading(false);
      isInitializingRef.current = false;
    }
  }, [open, invoice.id, useStripeFlow, stripeMode, paymentData, requestedAmount]);

  useEffect(() => {
    if (stripeMode === 'new_card' && useStripeFlow) {
      initializeCardPayment();
    }
  }, [stripeMode, useStripeFlow, initializeCardPayment]);

  // Reset stripe mode when payment type changes — don't auto-create PaymentIntent
  useEffect(() => {
    if (!useStripeFlow) {
      setStripeMode(null);
    }
  }, [useStripeFlow]);

  const handleSuccess = (isProcessing = false) => {
    if (isProcessing) {
      setPaymentProcessing(true);
    } else {
      setPaymentSuccess(true);
    }
    setTimeout(() => {
      onSuccess();
      onClose();
    }, isProcessing ? 3000 : 1500);
  };

  // When dialog opens in cardOnly mode, force credit_card type
  useEffect(() => {
    if (open && cardOnly) {
      setPaymentType('credit_card');
    }
  }, [open, cardOnly]);

  // Once the real list has LANDED, put the selection on something the server
  // would accept. `paymentType` seeds 'cash' (the historical hardcoded
  // default), so a clinic that deactivated Cash opened this dialog already
  // pointing at a method assertPaymentTypeAllowed rejects; a clinic with no
  // active type at all must end up on NO selection, which is what keeps the
  // Record button disabled. Only 'loaded' moves it — while in flight or
  // offline the fallback is the honest answer and must not be second-guessed.
  //
  // The two lists play DIFFERENT roles here. A selection is kept while it is
  // anywhere in `methodOptions`, so a user who deliberately picked Send Payment
  // Link or Apply Available Credit is not yanked off it. But a REPLACEMENT is
  // only ever drawn from `recordableClinicTypes`: the two synthetic entries are
  // flows, not clinic types, and auto-landing on one turns a dialog opened to
  // RECORD a payment into the Send-Link screen (Connect on) or into a button
  // reading "Apply $X Credit" (credit balance > 0) on exactly the loaded-empty
  // clinic that is supposed to end on no selection at all.
  useEffect(() => {
    if (!open || cardOnly) return;
    if (paymentTypesState !== 'loaded') return;
    const offered = methodOptionsKey ? methodOptionsKey.split('|') : [];
    const recordable = recordableTypesKey ? recordableTypesKey.split('|') : [];
    setPaymentType((current) => (offered.includes(current) ? current : recordable[0] ?? ''));
  }, [open, cardOnly, paymentTypesState, methodOptionsKey, recordableTypesKey]);

  // BILL-CRED: fetch the client's available credit so "Apply Available Credit"
  // can appear as a method option. Online-only (credit application is a money
  // mutation the op queue deliberately excludes).
  useEffect(() => {
    if (!open || !invoice.clientId || !skipWhenOffline()) {
      setCreditBalance(0);
      setCreditFetchFailed(false);
      return;
    }
    setCreditFetchFailed(false);
    creditsApi.ledger(invoice.clientId)
      .then((res) => setCreditBalance(res.balance || 0))
      .catch(() => {
        setCreditBalance(0);
        setCreditFetchFailed(true);
      });
  }, [open, invoice.clientId]);

  const handleClose = () => {
    if (processing) return;
    setPaymentType(cardOnly ? 'credit_card' : 'cash');
    setAmount('');
    setPaymentDate('');
    setReferenceNote('');
    setPayerName('');
    setError(null);
    setPaymentSuccess(false);
    setPaymentProcessing(false);
    setBalance(null);
    setStripeMode(null);
    setPaymentData(null);
    setStripePromise(null);
    setPaymentLinkAmount('');
    setPaymentLinkDialogOpen(false);
    setPaymentLinkSent(false);
    initializedForInvoiceRef.current = null;
    isInitializingRef.current = false;
    onClose();
  };

  const handleRecordManualPayment = async () => {
    // Belt to the disabled button: with no active clinic type there is no
    // selection, and posting '' would only earn a 400 from
    // assertPaymentTypeAllowed.
    if (!isCreditApply && !paymentType) {
      setError(
        'This clinic has no active payment types. Add or reactivate one under Settings → Invoicing first.',
      );
      return;
    }
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // Offline pre-validation mirroring the server's balance-due cap: the field
    // must not get a success UX for cash the server will later reject.
    if (!skipWhenOffline() && balance && numAmount > balance.balanceDue + 0.005) {
      setError(
        `Payment amount ($${numAmount.toFixed(2)}) exceeds balance due ($${balance.balanceDue.toFixed(2)})`,
      );
      return;
    }

    // BILL-CRED: applying credit is its own endpoint — it writes the Payment
    // row AND the 'applied' ledger entry in one transaction. Online-only.
    if (isCreditApply) {
      if (numAmount > creditBalance + 0.005) {
        setError(
          `Amount ($${numAmount.toFixed(2)}) exceeds available credit ($${creditBalance.toFixed(2)})`,
        );
        return;
      }
      setProcessing(true);
      setError(null);
      try {
        // Idempotency key held in a ref so it SURVIVES an error-retry (a lost response followed
        // by clicking Apply again must replay, not debit the credit twice — credit-integrity F3)
        // but is reminted when the amount changes and cleared on success. NOT derived from
        // (invoice, amount): a stable derived key would collide with a legitimate later
        // application of the same amount to the same invoice and silently replay it.
        const amountCents = toCents(numAmount);
        if (
          !applyKeyRef.current ||
          applyKeyRef.current.amountCents !== amountCents ||
          // Keyed on the INVOICE too (review): the same dialog instance applying the same amount
          // to a DIFFERENT invoice after an error is a NEW application, not a retry — without
          // this the server would correctly 409 a legitimate request with no way out.
          applyKeyRef.current.invoiceId !== invoice.id
        ) {
          applyKeyRef.current = {
            invoiceId: invoice.id,
            amountCents,
            key:
              typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          };
        }
        await creditsApi.apply({
          clientId: invoice.clientId!,
          allocations: [{ invoiceId: invoice.id, amount: numAmount }],
          requestId: applyKeyRef.current.key,
        });
        applyKeyRef.current = null; // success — the next application is a NEW request
        // The client's Available Credit card (Clients page) reads
        // ['client-credits', clientId] — invalidate or it serves the stale
        // pre-apply balance for the staleTime window.
        queryClient.invalidateQueries(['client-credits', invoice.clientId]);
        handleSuccess();
      } catch (err: any) {
        console.error('Apply credit error:', err);
        setError(err.response?.data?.message || 'Failed to apply credit');
        setProcessing(false);
      }
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const payload = {
        invoiceId: invoice.id,
        amount: numAmount, // DOLLARS — the server converts to cents
        paymentType,
        // `Payment.paymentDate` is a CALENDAR day when it comes from this field: the
        // input is `type="date"`, so it hands back a bare `yyyy-MM-dd`. Posting that
        // string made the server store UTC MIDNIGHT, which every negative-offset
        // reader shows as the PREVIOUS day — the reported bug. Post the noon anchor
        // instead (DATE-KINDS §2.5 W6/W10); blank still means "now", an INSTANT.
        paymentDate: ymdToNoonUtcIso(paymentDate) || undefined,
        referenceNote: referenceNote.trim() || undefined,
        payerName: payerName.trim() || undefined,
      };
      // Offline-capable: a manual (cash/check) payment queues and syncs through
      // the server's full money rules (balance cap, already-paid guard, invoice
      // status recalc). Card/Stripe never enters this path.
      const result: any = await executeWrite({
        entityType: 'payment',
        table: 'payments',
        payload,
        networkFn: () => api.post('/payments/record', payload).then((r) => r.data),
        // The optimistic REPLICA row must mirror a server Payment row (Int CENTS,
        // succeeded) so the offline balance reader computes correctly. The queued
        // op payload keeps DOLLARS for the server's own conversion.
        extra: {
          amount: toCents(numAmount),
          status: 'succeeded',
          paymentMethod: paymentType,
          // Mirror the payload above so the optimistic replica row carries the
          // same value the server will store — otherwise the offline render and
          // the post-sync render disagree about the day.
          paymentDate: ymdToNoonUtcIso(paymentDate) || new Date().toISOString(),
          processedAt: new Date().toISOString(),
          payerName: payerName.trim() || null,
        },
      });
      // No direct invoice patch needed: the invoice's offline display status is
      // DERIVED from the optimistic payment row (readers.hydrateInvoice), so it
      // shows paid immediately and self-heals if the payment op is discarded.
      void result;
      handleSuccess();
    } catch (err: any) {
      console.error('Record payment error:', err);
      if (err instanceof OfflineWriteBlockedError || err?.name === 'OfflineWriteBlockedError') {
        setError(err.message);
      } else {
        setError(err.response?.data?.message || 'Failed to record payment');
      }
      setProcessing(false);
    }
  };

  const handleAddNewCard = () => setSaveCardDialogOpen(true);

  const handleSaveCardSuccess = () => {
    setSaveCardDialogOpen(false);
    setRefreshCardsKey((prev) => prev + 1);
  };

  const formatCurrency = formatDollars;

  const balanceDue = balance?.balanceDue ?? invoice.amount;
  const effectiveAmount = requestedAmount ?? balanceDue;
  const amountInCents = toCents(effectiveAmount);

  const getPaymentTypeLabel = (type: string) =>
    (clinicTypes ?? []).find((t) => t.value === type)?.label ||
    PAYMENT_TYPES.find((t) => t.value === type)?.label ||
    type;
  // With no active clinic type there is no selection to name, so the (disabled)
  // button reads "Record Payment" rather than "Record  Payment".
  const recordButtonLabel = paymentType
    ? `Record ${getPaymentTypeLabel(paymentType)} Payment`
    : 'Record Payment';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown={processing}>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">{cardOnly ? 'Enter Card Details' : 'Record Payment'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Invoice #{invoice.invoiceNumber}
            {invoice.horseName && ` · ${invoice.horseName}`}
            {invoice.clientName && ` · ${invoice.clientName}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mt: 1.5, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>Total</Typography>
              <Typography variant="subtitle1" fontWeight="bold">{formatCurrency(balance?.totalWithTax ?? invoice.amount)}</Typography>
            </Box>
            {balance && balance.totalPaid > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>Paid</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="success.main">{formatCurrency(balance.totalPaid)}</Typography>
              </Box>
            )}
            {balance && (balance.processingAmount ?? 0) > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>Processing</Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="info.main">{formatCurrency(balance.processingAmount!)}</Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                {requestedAmount != null ? 'Charging' : 'Balance Due'}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color={requestedAmount != null ? 'success.main' : 'primary'}>
                {formatCurrency(effectiveAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />

      {(paymentSuccess || paymentProcessing) ? (
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            {paymentProcessing ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>Bank transfer initiated!</Alert>
                <Typography color="text.secondary">
                  ACH payments typically take 1–4 business days to complete. The invoice will be updated automatically once the transfer settles.
                </Typography>
              </>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>Payment recorded successfully!</Alert>
                <Typography color="text.secondary">The invoice has been updated.</Typography>
              </>
            )}
          </Box>
        </DialogContent>
      ) : balanceLoading ? (
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {balance && (balance.processingAmount ?? 0) > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              A bank transfer of {formatCurrency(balance.processingAmount!)} is currently processing for this invoice.
              ACH payments typically take 1–4 business days to settle.
            </Alert>
          )}

          {/* Payment Type Selector — hidden in cardOnly mode */}
          {!cardOnly && (
            <>
              <TextField
                select
                fullWidth
                label="Payment Method"
                value={paymentType}
                onChange={(e) => {
                  setPaymentType(e.target.value);
                  setError(null);
                }}
                sx={{ mb: 2 }}
              >
                {methodOptions.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.value === 'payment_link' && <LinkIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* GET /payment-types is active-only, so [] from a LOADED query
                  means the clinic has retired every type. Say so instead of
                  listing built-ins the server would reject on save. */}
              {noRecordableClinicTypes && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This clinic has no active payment types. Add or reactivate one under Settings →
                  Invoicing before recording a payment.
                </Alert>
              )}

              {creditFetchFailed && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Couldn't load this client's account credit — the Apply Available Credit
                  option may be missing. Retry by reopening the dialog.
                </Alert>
              )}

              {!skipWhenOffline() && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You're offline — card payments and payment links need a connection. Cash and
                  check payments are recorded now and sync automatically when you reconnect.
                  Custom payment types configured in Settings may be unavailable until you're
                  back online.
                </Alert>
              )}

              {!isPaymentLink && <Divider sx={{ mb: 2 }} />}
            </>
          )}

          {/* --- Payment Link Flow --- */}
          {isPaymentLink && (
            <Box sx={{ py: 1 }}>
              <TextField
                fullWidth
                label="Amount to Request"
                type="number"
                value={paymentLinkAmount}
                onChange={(e) => setPaymentLinkAmount(e.target.value)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
                }}
                inputProps={{ min: 0.01, step: 0.01 }}
                helperText={`Balance due: ${formatCurrency(balanceDue)}. Leave as-is for full balance or enter a partial amount.`}
                sx={{ mb: 2.5 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, textAlign: 'center' }}>
                The link expires in 24 hours. The client can pay with card or bank transfer.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<LinkIcon />}
                onClick={() => setPaymentLinkDialogOpen(true)}
              >
                Send Payment Link{paymentLinkAmount ? ` — ${formatCurrency(parseFloat(paymentLinkAmount))}` : ''}
              </Button>
            </Box>
          )}

          {/* --- Stripe Card Flow --- */}
          {useStripeFlow && (
            <Box>
              {/* Toggle between new card/bank and on file */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant={stripeMode === 'new_card' ? 'contained' : 'outlined'}
                  startIcon={<CreditCardIcon />}
                  onClick={() => setStripeMode('new_card')}
                >
                  New Card / Bank
                </Button>
                {invoice.clientId && (
                  <Button
                    size="small"
                    variant={stripeMode === 'on_file' ? 'contained' : 'outlined'}
                    startIcon={<OnFileIcon />}
                    onClick={() => setStripeMode('on_file')}
                  >
                    On File
                  </Button>
                )}
              </Box>

              {stripeMode === null && (
                <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <Typography variant="body2">
                    Choose a payment method above to continue
                  </Typography>
                </Box>
              )}

              {stripeMode === 'new_card' && (
                <>
                  {cardLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                      <CircularProgress sx={{ mb: 2 }} />
                      <Typography color="text.secondary">Initializing payment...</Typography>
                    </Box>
                  ) : paymentData && stripePromise ? (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret: paymentData.clientSecret,
                        appearance: {
                          theme: 'stripe',
                          variables: { colorPrimary: '#C9772B', fontFamily: 'Roboto, sans-serif' },
                        },
                      }}
                    >
                      <CardPaymentForm
                        amount={amountInCents}
                        paymentId={paymentData.paymentId}
                        onSuccess={handleSuccess}
                        onCancel={handleClose}
                      />
                    </Elements>
                  ) : error ? (
                    <Box sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button variant="contained" onClick={initializeCardPayment}>Try Again</Button>
                      </Box>
                    </Box>
                  ) : null}
                </>
              )}

              {stripeMode === 'on_file' && invoice.clientId && (
                <OnFilePayment
                  amount={amountInCents}
                  clientId={invoice.clientId}
                  invoiceId={invoice.id}
                  onSuccess={handleSuccess}
                  onCancel={handleClose}
                  onAddNew={handleAddNewCard}
                  refreshKey={refreshCardsKey}
                />
              )}
            </Box>
          )}

          {/* --- Manual Recording Form (also hosts the Apply-Credit flow) --- */}
          {!useStripeFlow && !isPaymentLink && (
            <Box>
              {isCreditApply && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {formatDollars(creditBalance)} of account credit is available for{' '}
                  {invoice.clientName || 'this client'}. The applied amount settles this
                  invoice and is deducted from their credit balance.
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
                  }}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  helperText={isCreditApply
                    ? `Up to ${formatDollars(Math.min(creditBalance, balance?.balanceDue ?? creditBalance))}`
                    : undefined}
                  sx={{ flex: 1 }}
                />
                {!isCreditApply && (
                  <TextField
                    label="Date Received"
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                )}
              </Box>

              {!isCreditApply && (
                <>
                  <TextField
                    fullWidth
                    label="Reference / Note"
                    value={referenceNote}
                    onChange={(e) => setReferenceNote(e.target.value)}
                    placeholder={REFERENCE_PLACEHOLDERS[paymentType] || ''}
                    helperText="Optional — check number, transaction ID, username, etc."
                    sx={{ mb: 1 }}
                  />

                  <TextField
                    fullWidth
                    label="Paid by (optional)"
                    value={payerName}
                    onChange={(e) => setPayerName(e.target.value)}
                    placeholder="If someone other than the client paid"
                    helperText="Records a third-party payer on the receipt"
                    sx={{ mb: 1 }}
                  />
                </>
              )}

              <DialogActions sx={{ mt: 2, px: 0 }}>
                <Button onClick={handleClose} disabled={processing}>Cancel</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRecordManualPayment}
                  // `!paymentType` is the whole of the loaded-empty block: with
                  // no active clinic type the select carries no selection, so a
                  // manual record can't be submitted (BulkMarkPaid's pattern).
                  // Apply-Credit is a flow, not a ClinicPaymentType, so it is
                  // never blocked by an empty type list.
                  disabled={processing || !amount || (!isCreditApply && !paymentType)}
                >
                  {processing ? (
                    <><CircularProgress size={20} sx={{ mr: 1 }} />{isCreditApply ? 'Applying...' : 'Recording...'}</>
                  ) : isCreditApply ? (
                    `Apply ${amount ? formatDollars(parseFloat(amount) || 0) : ''} Credit`
                  ) : (
                    recordButtonLabel
                  )}
                </Button>
              </DialogActions>
            </Box>
          )}
        </DialogContent>
      )}

      {invoice.clientId && (
        <SavePaymentMethodDialog
          open={saveCardDialogOpen}
          onClose={() => setSaveCardDialogOpen(false)}
          onSuccess={handleSaveCardSuccess}
          client={{ id: invoice.clientId, name: invoice.clientName || 'Client' }}
        />
      )}

      <SendPaymentLinkDialog
        open={paymentLinkDialogOpen}
        onClose={() => {
          setPaymentLinkDialogOpen(false);
          if (paymentLinkSent) {
            setPaymentLinkSent(false);
            onSuccess();
            onClose();
          }
        }}
        onSuccess={() => {
          // Stay on the sent step so the confirmation and the link show; Done closes both.
          setPaymentLinkSent(true);
          onPaymentLinkSent?.();
        }}
        invoice={{
          id: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          amount: invoice.amount,
          taxAmount: invoice.taxAmount,
          status: 'pending',
          dueDate: invoice.dueDate || new Date().toISOString(),
          horse: invoice.horse || { name: invoice.horseName || 'Unknown' },
          client: invoice.client,
          thirdPartyPayer: invoice.thirdPartyPayer,
          paymentLinkUrl: invoice.paymentLinkUrl,
        }}
        balanceDue={paymentLinkAmount ? parseFloat(paymentLinkAmount) : balance?.balanceDue}
      />
    </Dialog>
  );
};

export default EnhancedPaymentDialog;
